import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

// MUI components
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputAdornment,
  Tooltip,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  useMediaQuery,
  Tab,
  Tabs,
  TextField,
  Switch,
  FormControlLabel,
  Typography,
} from "@mui/material";

// MUI styles and icons
import { useTheme } from "@mui/material/styles";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";

// React Hook Form and validation
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { Helmet } from "react-helmet-async";

// API functions
import {
  deleteCurrentUser,
  getUserSettings,
  updateUserSettings,
} from "../../api/users";

// Context/hooks
import { useAuth } from "../../context/AuthContext";

// Localization
import { useTranslation } from "react-i18next";

// Custom UI components
import MUIAlert from "../../components/UI/MUIAlert";
import FormError from "../../components/Forms/FormError";

const MySettings = () => {
  const { t } = useTranslation();
  const theme = useTheme();

  const userSchema = yup.object().shape({
    firstName: yup.string().trim().required(t("errors.first_name_required")),
    lastName: yup.string().trim().required(t("errors.last_name_required")),
    phone: yup
      .string()
      .transform((value) => value?.replace(/\s+/g, "")) // remove spaces
      .matches(/^\+?[0-9]{8,15}$/, t("errors.phone_invalid"))
      .required(t("errors.phone_required")),
    email: yup
      .string()
      .trim()
      .lowercase()
      .email(t("errors.email_invalid"))
      .required(t("errors.email_required")),
    legalStatus: yup.string().required(),
    billingAddress: yup.string().nullable().trim(),
    vatNr: yup
      .string()
      .nullable()
      .trim()
      .matches(/^[A-Z0-9 ]*$/, t("errors.vat_invalid")),
    bankAccountNumber: yup
      .string()
      .nullable()
      .trim()
      .matches(/^[A-Z0-9 ]*$/, t("errors.bank_nr_invalid")),
    businessName: yup.string().nullable().trim(),
    bankPrefix: yup.string().nullable().trim(),
    receive_notifications: yup.string().nullable(),
  });

  const deleteSchema = yup.object().shape({
    deletionConfirmationPassword: yup
      .string()
      .min(6, t("errors.password_min"))
      .required(t("errors.password_required")),
  });

  const sections = [
    { id: "personal", label: t("user_settings.personal_title") },
    { id: "payment", label: t("user_settings.payment_title") },
    { id: "notifications", label: t("user_settings.notifications_title") },
    { id: "delete", label: t("user_settings.delete_title") },
  ];

  const location = useLocation();
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submittingPersonal, setsubmittingPersonal] = useState(false);
  const [submittingPayments, setSubmittingPayments] = useState(false);
  const [submittingNotifications, setSubmittingNotifications] = useState(false);
  const [submittingDeletion, setsubmittingDeletion] = useState(false);
  const [error, setError] = useState(null);
  const { logout } = useAuth();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [confirmParameter, setconfirmParameter] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success"); // Default severity

  const userFormMethods = useForm({
    resolver: yupResolver(userSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      businessName: "",
      vatNr: "",
      bankAccountNumber: "",
      bankPrefix: "",
      billingAddress: "",
      receive_notifications: "",
    },
  });

  const {
    register: registerUser,
    handleSubmit: handleUserSubmit,
    formState: { errors: userErrors },
    setValue: setUserValue,
    control: controlUser,
    reset,
  } = userFormMethods;

  const deleteFormMethods = useForm({
    resolver: yupResolver(deleteSchema),
  });

  const {
    register: registerDelete,
    handleSubmit: handleDelete,
    formState: { errors: deleteErrors },
    control: controlDelete,
  } = deleteFormMethods;

  const sectionRefs = useRef({});
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [expandedAccordion, setExpandedAccordion] = React.useState(0);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const handleTabChange = (_, newValue) => {
    setSelectedTab(newValue);
    setExpandedAccordion(newValue); // <-- this is key
    const sectionId = sections[newValue].id;
    sectionRefs.current[sectionId]?.scrollIntoView({ behavior: "smooth" });
  };

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const showConfirm = (title, content, onConfirm, parameter) => {
    setConfirmTitle(title);
    setConfirmContent(content);
    setConfirmCallback(() => onConfirm);
    setconfirmParameter(parameter);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmTitle("");
    setConfirmContent("");
    setConfirmCallback(null);
    setconfirmParameter(null);
  };

  const handleConfirmAction = () => {
    if (confirmCallback && confirmParameter) {
      confirmCallback(confirmParameter);
    }
    handleConfirmClose();
  };

  const handleAccountDelete = async (e) => {
    e.preventDefault();

    const formData = new FormData(e.target);
    const deletionConfirmationPassword = formData.get(
      "deletionConfirmationPassword"
    );

    // TODO show confirmation window
    if (!deletionConfirmationPassword) {
      showAlert(t("user_settings.delete_password_missing"), "error");
      return;
    }

    setsubmittingDeletion(true); // Set loading to true before async operation
    try {
      const user = await deleteCurrentUser(deletionConfirmationPassword);

      if (user._error) {
        showAlert(t("user_settings.delete_failure"), "error");
      } else {
        showAlert(t("user_settings.delete_success"));

        // TODO disable any interaction or switch to a spinner
        // Pause execution for 3 seconds (3000 milliseconds)
        await new Promise((resolve) => setTimeout(resolve, 3000));

        logout();
      }
    } catch (error) {
      showAlert(t("errors.err"), "error");
    } finally {
      setsubmittingDeletion(false);
    }
  };

  const onSubmit = async (data) => {
    setsubmittingPersonal(true);

    data.phone = settings.phone; // Keep the original phone number from settings
    data.email = settings.email; // Keep the original email from settings

    try {
      const user = await updateUserSettings(
        data.firstName,
        data.lastName,
        data.legalStatus,
        data.email,
        data.phone,
        data.billingAddress,
        data.vatNr,
        data.bankAccountNumber,
        data.businessName,
        data.bankPrefix,
        data.receiveNotifications
      );
      if (user) {
        showAlert(t("user_settings.update_success"));
        reset(data);
      } else {
        showAlert(t("errors.err"), "error");
      }
    } catch (error) {
      showAlert(t("errors.err"), "error");
    } finally {
      setsubmittingPersonal(false);
    }
  };

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const fetchedSettings = await getUserSettings();
        setSettings(fetchedSettings);

        // For personal
        setUserValue("firstName", fetchedSettings.first_name);
        setUserValue("lastName", fetchedSettings.last_name);
        setUserValue("legalStatus", fetchedSettings.legal_status);
        setUserValue("phone", fetchedSettings.phone);
        setUserValue("email", fetchedSettings.email);

        // For Notifications
        setUserValue(
          "receiveNotifications",
          fetchedSettings.receive_notifications
        );

        // For payments
        setUserValue("billingAddress", fetchedSettings.billing_address);
        setUserValue("vatNr", fetchedSettings.vat_nr);
        setUserValue("bankAccountNumber", fetchedSettings.bank_account_number);
        setUserValue("businessName", fetchedSettings.business_name);
        setUserValue("bankPrefix", fetchedSettings.bank_prefix);
      } catch (err) {
        setError(t("user_settings.fetch_settings_failed"));
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      const tabIndex = sections.findIndex((section) => section.id === tabParam);
      // alert(tabIndex);
      if (tabIndex !== -1) {
        setSelectedTab(tabIndex);
        setExpandedAccordion(tabIndex);
      }
    }
  }, [location.search, sections]);

  return (
    <>
      <Helmet>
        <title>{t("seo.title.settings")}</title>
        <meta property="og:title" content={t("seo.title.settings")} />

        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Paper
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" }, // column on xs, row on sm+
          p: { xs: 2, sm: 3 },
          minHeight: "80vh",
          maxWidth: "1000px",
          mx: "auto",
          width: "100%",
          borderRadius: { xs: 0, sm: "auto" },
          border: "none",
        }}
      >
        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />
        {/* Left Side Navigation */}
        <Tabs
          orientation={isMobile ? "horizontal" : "vertical"}
          value={selectedTab}
          onChange={handleTabChange}
          sx={{
            borderRight: isMobile ? "none" : 1,
            borderColor: isMobile ? "transparent" : "divider",
            minWidth: isMobile ? "auto" : 200,
            borderBottom: isMobile ? 1 : "none",
            borderColor: isMobile ? "divider" : "transparent",
            mb: isMobile ? 2 : 0,
            display: isMobile ? "none" : "auto",
          }}
        >
          {sections.map((section) => (
            <Tab key={section.id} label={section.label} />
          ))}
        </Tabs>

        {/* Right Side Content */}
        <Box sx={{ flex: 1, pl: { xs: 0, sm: 4 } }}>
          {/* Personal */}
          <Box
            ref={(el) => (sectionRefs.current["personal"] = el)}
            sx={{ mb: { xs: 1.5, sm: 3 } }}
          >
            <Accordion
              expanded={expandedAccordion === 0}
              onChange={() => setExpandedAccordion(0)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h4">
                  {t("user_settings.personal_title")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <form onSubmit={handleUserSubmit(onSubmit)}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Controller
                      name="firstName"
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.first_name")}
                          margin="normal"
                          size="normal"
                          error={!!userErrors.firstName}
                          helperText={userErrors.firstName?.message}
                        />
                      )}
                    />

                    <Controller
                      name="lastName"
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.last_name")}
                          error={!!userErrors.lastName}
                          helperText={userErrors.lastName?.message}
                          margin="normal"
                          size="normal"
                          slotProps={{ inputLabel: { shrink: true } }}
                        />
                      )}
                    />

                    <FormControl margin="normal" fullWidth>
                      <InputLabel id="property-type-label">
                        {t("forms.legal_status")}
                      </InputLabel>
                      <Controller
                        name="legalStatus"
                        control={controlUser}
                        defaultValue=""
                        render={({ field }) => (
                          <Select
                            labelId="legal-status-label"
                            label={t("forms.legal_status")}
                            size="normal"
                            {...field}
                            error={!!userErrors.legalStatus}
                            // InputLabelProps={{ shrink: true }}
                          >
                            <MenuItem value="private">
                              {t("forms.private_entity")}
                            </MenuItem>
                            <MenuItem value="business">
                              {t("forms.business_entity")}
                            </MenuItem>
                          </Select>
                        )}
                      />
                      <Typography variant="body2" color="error">
                        {userErrors.legalStatus?.message}
                      </Typography>
                    </FormControl>

                    <Controller
                      name="phone"
                      disabled
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.phone")}
                          error={!!userErrors.phone}
                          helperText={userErrors.phone?.message}
                          margin="normal"
                          size="normal"
                        />
                      )}
                    />

                    <Controller
                      name="email"
                      disabled
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.email")}
                          error={!!userErrors.email}
                          helperText={userErrors.email?.message}
                          margin="normal"
                          size="normal"
                        />
                      )}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Button
                      size="large"
                      type="submit"
                      variant="contained"
                      color="secondary"
                      sx={{
                        "&:hover": {
                          backgroundColor: "secondary.light",
                        },
                      }}
                    >
                      {submittingPersonal ? (
                        <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                      ) : (
                        t("forms.save")
                      )}
                    </Button>
                  </Box>
                </form>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Payments */}
          <Box
            ref={(el) => (sectionRefs.current["business"] = el)}
            sx={{ mb: { xs: 1.5, sm: 3 } }}
          >
            <Accordion
              expanded={expandedAccordion === 1}
              onChange={() => setExpandedAccordion(1)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h4">
                  {t("user_settings.payment_title")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography sx={{ mb: 2 }}>
                  {t("user_settings.payment_section_body")}
                </Typography>
                <form onSubmit={handleUserSubmit(onSubmit)}>
                  <Box
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    <Controller
                      name="businessName"
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.business_name")}
                          error={!!userErrors.businessName}
                          helperText={userErrors.businessName?.message}
                          margin="normal"
                          size="normal"
                        />
                      )}
                    />

                    <Controller
                      name="vatNr"
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.vat")}
                          error={!!userErrors.vatNr}
                          helperText={userErrors.vatNr?.message}
                          margin="normal"
                          size="normal"
                          InputProps={{
                            // Add InputProps to customize the input
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="LVXXXXXXXXXXX">
                                  <IconButton
                                    aria-label="vat number info"
                                    edge="end"
                                    size="small" // Make the icon small
                                  >
                                    <InfoIcon fontSize="small" />{" "}
                                    {/* Make the icon itself small */}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="bankPrefix"
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.bank_prefix")}
                          error={!!userErrors.bankPrefix}
                          helperText={userErrors.bankPrefix?.message}
                          margin="normal"
                          size="normal"
                          InputProps={{
                            // Add InputProps to customize the input
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title={t("forms.bank_prefix_tooltip")}>
                                  <IconButton
                                    aria-label="vat number info"
                                    edge="end"
                                    size="small" // Make the icon small
                                  >
                                    <InfoIcon fontSize="small" />{" "}
                                    {/* Make the icon itself small */}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="bankAccountNumber"
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.bank_account_nr")}
                          error={!!userErrors.bankAccountNumber}
                          helperText={userErrors.bankAccountNumber?.message}
                          margin="normal"
                          size="normal"
                          InputProps={{
                            // Add InputProps to customize the input
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="LV09 HABA 1111 2222 3333 4">
                                  <IconButton
                                    aria-label="vat number info"
                                    edge="end"
                                    size="small" // Make the icon small
                                  >
                                    <InfoIcon fontSize="small" />{" "}
                                    {/* Make the icon itself small */}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />

                    <Controller
                      name="billingAddress"
                      control={controlUser}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label={t("forms.billing_address")}
                          error={!!userErrors.billingAddress}
                          helperText={userErrors.billingAddress?.message}
                          margin="normal"
                          size="normal"
                          InputProps={{
                            // Add InputProps to customize the input
                            endAdornment: (
                              <InputAdornment position="end">
                                <Tooltip title="Rīga, Slokas Iela, LV-2222">
                                  <IconButton
                                    aria-label="vat number info"
                                    edge="end"
                                    size="small" // Make the icon small
                                  >
                                    <InfoIcon fontSize="small" />{" "}
                                    {/* Make the icon itself small */}
                                  </IconButton>
                                </Tooltip>
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />

                    <Divider sx={{ my: 2 }} />

                    <Button
                      size="large"
                      type="submit"
                      variant="contained"
                      color="secondary"
                      sx={{
                        "&:hover": {
                          backgroundColor: "secondary.light",
                        },
                      }}
                    >
                      {submittingPayments ? (
                        <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                      ) : (
                        t("forms.save")
                      )}
                    </Button>
                  </Box>
                </form>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Notifications */}
          <Box
            ref={(el) => (sectionRefs.current["notifications"] = el)}
            sx={{ mb: { xs: 1.5, sm: 3 } }}
          >
            <Accordion
              expanded={expandedAccordion === 2}
              onChange={() => setExpandedAccordion(2)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h4">
                  {t("user_settings.notifications_title")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <form onSubmit={handleUserSubmit(onSubmit)}>
                  <Controller
                    name="receiveNotifications"
                    control={controlUser}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            {...field}
                            checked={!!field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label={
                          field.value
                            ? t("user_settings.receive_notifications_toggle_on")
                            : t(
                                "user_settings.receive_notifications_toggle_off"
                              )
                        }
                      />
                    )}
                  />
                  <Divider sx={{ my: 2 }} />

                  <Button type="submit" variant="contained" color="primary">
                    {submittingPayments ? (
                      <CircularProgress size={24} color="inherit" /> // Show spinner when loading
                    ) : (
                      t("user_settings.receive_notifications_cta")
                    )}
                  </Button>
                </form>
              </AccordionDetails>
            </Accordion>
          </Box>

          {/* Delete Account */}
          <Box
            ref={(el) => (sectionRefs.current["delete"] = el)}
            sx={{ mb: { xs: 1.5, sm: 3 } }}
          >
            <Accordion
              expanded={expandedAccordion === 3}
              onChange={() => setExpandedAccordion(3)}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="h4">
                  {t("user_settings.delete_title")}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <form onSubmit={handleAccountDelete}>
                  <Typography sx={{ mb: 2 }}>
                    {t("user_settings.delete_section_warning")}
                  </Typography>
                  <Divider sx={{ my: 2 }} />

                  <Controller
                    name="deletionConfirmationPassword"
                    control={controlDelete}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label={t("forms.confirm_password")}
                        fullWidth
                        error={!!deleteErrors.deletionConfirmationPassword}
                        helperText={
                          deleteErrors.deletionConfirmationPassword?.message
                        }
                        margin="normal"
                        size="normal"
                      />
                    )}
                  />

                  <Button
                    sx={{ mt: 1 }}
                    size="large"
                    type="submit"
                    variant="contained"
                    color="error"
                    fullWidth
                  >
                    {t("user_settings.delete_title")}
                  </Button>
                </form>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default MySettings;
